import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  deleteDoc
} from 'firebase/firestore';
import { db } from './config';
import api from '../../shared/services/api';

/**
 * USER MANAGEMENT SERVICE
 * Multi-tenant user ve subscription yönetimi
 */
export const UserManagementService = {

  // Check if Firebase is available
  isFirebaseAvailable: () => {
    return db !== null;
  },

  /**
   * GET USER WITH BUSINESSES
   */
  getUser: async (userEmail) => {
    try {
      if (!UserManagementService.isFirebaseAvailable()) {
        console.warn('📝 Firebase not available');
        return null;
      }

      const userRef = doc(db, 'users', userEmail);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const businesses = await UserManagementService.getUserBusinesses(userData.businesses || []);

        return {
          id: userSnap.id,
          ...userData,
          businessesData: businesses
        };
      }
      return null;
    } catch (error) {
      console.error('❌ Error getting user:', error);
      throw error;
    }
  },

  /**
   * GET USER BUSINESSES DATA
   */
  getUserBusinesses: async (businessIds) => {
    try {
      if (!businessIds || businessIds.length === 0) return [];
      
      // 🚀 PARALLEL FETCH: İşletme detaylarını paralel çek
      const businesses = await Promise.all(businessIds.map(async (businessId) => {
        try {
          const restaurantRef = doc(db, 'restaurants', businessId);
          const restaurantSnap = await getDoc(restaurantRef);
          if (restaurantSnap.exists()) {
            return { id: restaurantSnap.id, ...restaurantSnap.data(), source: 'restaurants' };
          }

          const businessRef = doc(db, 'businesses', businessId);
          const businessSnap = await getDoc(businessRef);
          if (businessSnap.exists()) {
            return { id: businessSnap.id, ...businessSnap.data(), source: 'businesses' };
          }
          return null;
        } catch (e) {
          console.warn(`⚠️ Error fetching biz ${businessId}:`, e.message);
          return null;
        }
      }));

      return businesses.filter(Boolean);
    } catch (error) {
      console.error('❌ Error getting user businesses:', error);
      return [];
    }
  },

  /**
   * AUTHORIZE CARI USER
   */
  authorizeCariUser: async (userData, businessId, customerId) => {
    try {
      console.log('👤 Authorizing cari user via backend:', userData.email);

      const response = await api.post('/auth/create-cari-user', {
        email: userData.email,
        oldEmail: userData.oldEmail,
        name: userData.name,
        password: userData.password,
        businessId: businessId,
        customerId: customerId
      });

      const data = response.data;
      if (!data || data.error) {
        throw new Error(data?.error || 'Yetki verilirken bir hata oluştu');
      }

      console.log('✅ Cari user authorized successfully');
      return true;
    } catch (error) {
      console.error('❌ Error authorizing cari:', error);
      const errMsg = error.response?.data?.error || error.message || 'Yetki verilirken bir hata oluştu';
      throw new Error(errMsg);
    }
  },

  /**
   * GET CARI USER BY CUSTOMER ID
   */
  getCariByCustomerId: async (customerId) => {
    try {
      if (!UserManagementService.isFirebaseAvailable()) return null;
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('associatedCariId', '==', customerId)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('❌ Error getting cari user:', error);
      return null;
    }
  },

  /**
   * REVOKE CARI ACCESS
   */
  revokeCariAccess: async (email) => {
    try {
      const userRef = doc(db, 'users', email);
      // Yetkiyi silmek yerine pasif yapabiliriz veya dökümanı silebiliriz.
      // Kullanıcı talebine göre "iptal et" dendiği için dökümanı status=inactive yapalım.
      await updateDoc(userRef, {
        status: 'inactive',
        role: 'guest', // Rolü düşür
        associatedCariId: null, // Bağlantıyı kes
        updated_at: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('❌ Error revoking cari access:', error);
      throw error;
    }
  },

  /**
   * Legacy and other methods (Keeping for compatibility)
   */
  checkSubscriptionStatus: async (userEmail) => {
    const userSnapshot = await UserManagementService.getUser(userEmail);
    if (!userSnapshot) return { isActive: false, reason: 'no_user' };
    return { isActive: true, plan: userSnapshot.subscription?.plan || 'free' };
  }
};

export default UserManagementService;